import pdfMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { useStore } from 'vuex'
import useDate from '@/services/utils/day'
import { commaFormat } from '@/services/utils/global';

pdfMake.vfs = pdfFonts.pdfMake.vfs

const pdfPrinter = () => {
  const store = useStore()
  const { formatDate } = useDate()

  const userName = store.state.user.user.name

  const exportToPDF = (company, flatRows, dateRange) => {
    const doc = {
      pageSize: 'A4',
      pageMargins: [30, 100, 30, 80],
      pageOrientation: 'landscape',
      header: getHeader(company),
      footer: getFooter(userName),
      content: getContent(flatRows, dateRange),

      defaultStyle: {
        fontSize: 10,
        color: 'black'
      },

      styles: {
        title: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 10],
          decoration: 'underline'
        },
        tableHeader: {
          bold: true,
          fillColor: '#f3f2f7'
        }
      },

      info: {
        title: 'Organization Activity Report'
      }
    }

    pdfMake.createPdf(doc).open()
  }

  /* ================= CONTENT ================= */

  const getContent = (flatRows, dateRange) => {
    return [
      {
        text: 'Organization Activity Report',
        style: 'title'
      },

      {
        stack: [
          { text: `Date: ${dateRange.start} - ${dateRange.end}` }
        ]
      },

      {
        margin: [0, 10, 0, 0],
        table: {
          headerRows: 1,
          widths: ['*', 70, 70, 80, 70, 70, 80],
          body: getTableBody(flatRows)
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex === 0 ? '#f3f2f7' : null),
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5
        }
      }
    ]
  }

  /* ================= TABLE ================= */

  const getTableBody = (flatRows) => {
    const topLevelGroups = flatRows.filter(row => row.level === 0 && row.type === 'group')
    
    return [
      getTableHead(),
      ...getTableRows(flatRows),
      ...(topLevelGroups.length > 0 ? [getTotalRow(flatRows)] : [])
    ]
  }

  const getTableHead = () => {
    return [
      { text: 'Activity', style: 'tableHeader' },
      { text: 'Total Hours', style: 'tableHeader', alignment: 'right' },
      { text: 'Approved Hours', style: 'tableHeader', alignment: 'right' },
      { text: 'Non Approved Hours', style: 'tableHeader', alignment: 'right' },
      { text: 'Total Cost', style: 'tableHeader', alignment: 'right' },
      { text: 'Approved Cost', style: 'tableHeader', alignment: 'right' },
      { text: 'Non Approved Cost', style: 'tableHeader', alignment: 'right' }
    ]
  }

  const getTotalRow = (flatRows) => {
    const topLevelGroups = flatRows.filter(row => row.level === 0 && row.type === 'group')
    
    const total = {
      total_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.total_hours) || 0), 0),
      approved_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.approved_hours) || 0), 0),
      completed_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.completed_hours) || 0), 0),
      total_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.total_cost) || 0), 0),
      approved_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.approved_cost) || 0), 0),
      completed_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.completed_cost) || 0), 0)
    }

    return [
      { text: 'Total', bold: true, alignment: 'right' },
      { text: total.total_hours, alignment: 'right', bold: true },
      { text: total.approved_hours, alignment: 'right', bold: true },
      { text: total.completed_hours, alignment: 'right', bold: true },
      { text: commaFormat(total.total_cost), alignment: 'right', bold: true },
      { text: commaFormat(total.approved_cost), alignment: 'right', bold: true },
      { text: commaFormat(total.completed_cost), alignment: 'right', bold: true }
    ]
  }

  const getTableRows = (flatRows) => {
    return flatRows.map(row => ([
      {
        text: row.name,
        margin: [row.level * 10, 0, 0, 0],
        bold: row.type === 'group'
      },
      { text: row.total_hours || 0, alignment: 'right' },
      { text: row.approved_hours || 0, alignment: 'right' },
      { text: row.completed_hours || 0, alignment: 'right' },
      { text: commaFormat(row.total_cost) || 0, alignment: 'right' },
      { text: commaFormat(row.approved_cost) || 0, alignment: 'right' },
      { text: commaFormat(row.completed_cost) || 0, alignment: 'right' }
    ]))
  }

  /* ================= HEADER ================= */

  const getHeader = (company) => {
    return {
      margin: [30, 25, 30, 10],
      columns: [
        {
          image: company.logo64,
          width: 80
        },
        {
          alignment: 'right',
          stack: [
            { text: company.name, bold: true },
            `Address: ${company.address}`,
            `Phone: ${company.phone}`,
            `Email: ${company.email}`
          ]
        }
      ]
    }
  }

  /* ================= FOOTER ================= */

  const getFooter = (userName) => {
    return {
      margin: [25, 0],
      columns: [
        {
          width: '70%',
          text: 'N.B: This is a system generated document and requires no manual signature.',
          fontSize: 8,
          margin: [0, 40, 0, 0]
        },
        {
          width: '30%',
          alignment: 'right',
          fontSize: 8,
          margin: [0, 20, 0, 0],
          stack: [
            { text: 'Printed by', bold: true },
            userName,
            formatDate(new Date())
          ]
        }
      ]
    }
  }

  return {
    exportToPDF
  }
}

export default pdfPrinter;
